import { useEffect, useState, useMemo } from 'react';
import { Search, Play, VideoOff, Check, Flame } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { api, errMsg } from '@/lib/api';
import { useI18n } from '@/lib/i18n';
import { isValidYouTube } from '@/lib/youtube';
import { LoadingScreen } from '@/components/State';
import { toast } from 'sonner';

// Reusable coach-only picker for the existing Exercise / Meal libraries.
// type: 'exercise' | 'meal'. onSelect(item) returns the chosen master doc.
export default function LibraryPickerModal({ open, onOpenChange, type, initialFilter, onSelect }) {
  const { t } = useI18n();
  const [items, setItems] = useState(null);
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    if (!open) return;
    setFilter(initialFilter || 'all');
    setQ('');
    if (items) return;
    (async () => {
      try {
        const url = type === 'meal' ? '/meal-library' : '/exercise-library';
        const res = await api.get(url);
        setItems(res.data?.exercises || res.data?.meals || res.data || []);
      } catch (e) { toast.error(errMsg(e)); setItems([]); }
    })();
    // eslint-disable-next-line
  }, [open]);

  const groups = useMemo(() => {
    if (!items) return ['all'];
    const key = type === 'meal' ? 'category' : 'muscle_group';
    return ['all', ...Array.from(new Set(items.map((i) => i[key]).filter(Boolean)))];
  }, [items, type]);

  const filtered = useMemo(() => {
    if (!items) return [];
    const key = type === 'meal' ? 'category' : 'muscle_group';
    return items.filter((i) =>
      (filter === 'all' || i[key] === filter) &&
      (i.name || '').toLowerCase().includes(q.toLowerCase())
    );
  }, [items, filter, q, type]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[88vh] flex-col overflow-hidden border-white/10 bg-asgym-surface p-0 text-asgym-text sm:max-w-2xl" data-testid="library-picker">
        <DialogHeader className="border-b border-white/[0.06] px-5 py-4">
          <DialogTitle className="font-display text-lg font-extrabold tracking-tight text-white">
            {type === 'meal' ? t('picker.chooseMeal') : t('picker.chooseExercise')}
          </DialogTitle>
          <DialogDescription className="sr-only">{t('picker.hint')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-3 px-5 pt-4">
          <div className="flex items-center gap-2 rounded-xl border border-white/[0.06] bg-black/30 px-3">
            <Search size={16} className="text-asgym-muted" />
            <input autoFocus className="h-11 flex-1 bg-transparent text-sm text-white placeholder:text-asgym-muted outline-none" placeholder={t('picker.search')} value={q} onChange={(e) => setQ(e.target.value)} data-testid="picker-search" />
          </div>
          <div className="flex flex-wrap gap-1.5">
            {groups.map((g) => (
              <button key={g} onClick={() => setFilter(g)} className={`as-chip capitalize ${filter === g ? 'border-asgym-accent bg-asgym-accent/15 text-asgym-accent' : 'border-white/[0.08] bg-white/[0.03] text-asgym-sub'}`}>
                {g === 'all' ? t('picker.all') : g}
              </button>
            ))}
          </div>
        </div>

        <div className="min-h-0 flex-1 space-y-2 overflow-y-auto px-5 py-4">
          {items === null ? (
            <LoadingScreen />
          ) : filtered.length === 0 ? (
            <p className="py-10 text-center text-sm text-asgym-sub">{t('picker.noResults')}</p>
          ) : (
            filtered.map((it) => {
              const hasVideo = type === 'exercise' && isValidYouTube(it.video_url);
              return (
                <button
                  key={it.id}
                  onClick={() => { onSelect(it); onOpenChange(false); }}
                  data-testid={`picker-item-${it.id}`}
                  className="flex w-full items-center gap-3 rounded-xl border border-white/[0.06] bg-black/20 p-3.5 text-left transition-colors hover:border-asgym-accent/40 hover:bg-asgym-accent/[0.06]"
                >
                  {type === 'meal' && (
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-asgym-accent/10 text-asgym-accent"><Flame size={16} /></span>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[14px] font-bold text-white">{it.name}</p>
                    <p className="truncate text-[11px] text-asgym-muted capitalize">
                      {type === 'meal'
                        ? `${it.category || ''} · ${it.calories || 0} kcal · P${it.protein || 0} C${it.carbs || 0} F${it.fat || 0}`
                        : `${it.muscle_group || ''} · ${it.device || ''}`}
                    </p>
                  </div>
                  {type === 'exercise' && (
                    hasVideo
                      ? <Play size={15} className="shrink-0 text-asgym-accent" fill="currentColor" />
                      : <VideoOff size={15} className="shrink-0 text-asgym-muted" />
                  )}
                  <Check size={16} className="shrink-0 text-asgym-muted" />
                </button>
              );
            })
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
